import { useState, useEffect } from 'react';
import { Card } from 'antd';
import { useHistory, useParams } from 'react-router';
import { LoadingOutlined, PushpinFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../hooks';
import { Button } from 'components/ui/button';
import TemplateTaskCount from '../AuditTemplates/List/TemplateTaskCount';
import ScheduleModal from '../AuditSchedule/ScheduleModal';
import { useDispatch } from 'react-redux';
import { addAuditSchedule } from '../../modules/auditSchedule/actions';
import { AuditSchedule, AuditSourceType } from 'modules/auditSchedule/types';
import { StyleGuide } from 'styles/StyleGuide';
import firebase from 'firebase/compat/app';
import { AuditTemplate } from 'modules/auditTemplates/types';
import { GroupAuditTemplate } from 'modules/groupAuditTemplates/types';
import { fetchAuditTemplates } from 'modules/auditTemplates/actions';
import { fetchGroupAuditTemplates } from 'modules/groupAuditTemplates/actions';

export default function PinnedTemplatesWidget() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { orgId } = useParams<{ orgId: string }>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedAuditSourceType, setSelectedAuditSourceType] = useState<
    AuditSourceType | undefined
  >(undefined);
  const [auditTemplates, setAuditTemplates] = useState<AuditTemplate[]>([]);
  const [groupAuditTemplates, setGroupAuditTemplates] = useState<GroupAuditTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const { groupId, userId, userName } = useAppSelector((state) => ({
    groupId: state.orgs.currentOrg.groupId,
    userId: state.user.details.uid,
    userName: state.user.details.displayName || state.user.details.email,
  }));

  useEffect(() => {
    if (!orgId) {
      return;
    }
    dispatch(fetchAuditTemplates({ orgId }));
  }, [dispatch, orgId]);

  useEffect(() => {
    if (!groupId) {
      return;
    }
    dispatch(fetchGroupAuditTemplates({ groupId }));
  }, [dispatch, groupId]);

  // Fetch pinned org templates
  useEffect(() => {
    if (!orgId) return;

    const unsubscribe = firebase
      .firestore()
      .collection('orgs')
      .doc(orgId)
      .collection('auditTemplates')
      .where('isPinned', '==', true)
      .onSnapshot(
        (snapshot) => {
          const templates = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as AuditTemplate[];
          setAuditTemplates(templates);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching pinned templates:', error);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, [orgId]);

  // Fetch pinned group templates
  useEffect(() => {
    if (!groupId) {
      setGroupAuditTemplates([]);
      return;
    }

    const unsubscribe = firebase
      .firestore()
      .collection('groups')
      .doc(groupId)
      .collection('groupAuditTemplates')
      .where('isPinned', '==', true)
      .onSnapshot(
        (snapshot) => {
          const templates = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as GroupAuditTemplate[];
          setGroupAuditTemplates(templates);
        },
        (error) => {
          console.error('Error fetching pinned group templates:', error);
        }
      );

    return () => unsubscribe();
  }, [groupId]);

  const allPinnedTemplates = [...auditTemplates, ...groupAuditTemplates].sort((a, b) => {
    const aTitle = a?.title?.toLowerCase();
    const bTitle = b?.title?.toLowerCase();
    if (aTitle > bTitle) return 1;
    if (aTitle < bTitle) return -1;
    return 0;
  });

  const handleStartAudit = (templateId: string, isGroupTemplate: boolean) => {
    setSelectedTemplateId(templateId);
    setSelectedAuditSourceType(isGroupTemplate ? AuditSourceType.GROUP_TEMPLATE : undefined);
    setSelectedDate(new Date());
  };

  const handleCloseModal = () => {
    setSelectedDate(null);
    setSelectedTemplateId(null);
    setSelectedAuditSourceType(undefined);
  };

  const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const toDateValue = (value: any): Date | null => {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value?.toDate === 'function') {
      return value.toDate();
    }
    return null;
  };

  const findCreatedAuditId = async ({
    templateId,
    dueDate,
    startedAt,
  }: {
    templateId: string;
    dueDate: Date;
    startedAt: Date;
  }): Promise<string | null> => {
    if (!orgId) {
      return null;
    }

    const maxAttempts = 20;
    const dueDateMillis = dueDate.getTime();
    const minCreateTimeMillis = startedAt.getTime() - 120000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const snapshot = await firebase
        .firestore()
        .collection('orgs')
        .doc(orgId)
        .collection('auditSchedules')
        .orderBy('createTime', 'desc')
        .limit(30)
        .get();

      const match = snapshot.docs.find((doc) => {
        const row = doc.data() as any;
        if (row?.templateId !== templateId) {
          return false;
        }

        const rowDueDate = toDateValue(row?.dueDate);
        const rowCreateTime = toDateValue(row?.createTime);
        if (!rowDueDate || !rowCreateTime) {
          return false;
        }

        const dueDateMatches = Math.abs(rowDueDate.getTime() - dueDateMillis) < 60000;
        const createdRecently = rowCreateTime.getTime() >= minCreateTimeMillis;
        return dueDateMatches && createdRecently;
      });

      if (match) {
        return match.id;
      }

      await wait(500);
    }

    return null;
  };

  const handleAddAuditSchedule = async (data: {
    taskTitle?: string;
    templateId: string;
    recurringOption: AuditSchedule['recurringOption'];
    dueDate: Date;
    notifyDaysAfterDueDate?: AuditSchedule['notifyDaysAfterDueDate'];
    startDaysBeforeDueDate?: AuditSchedule['startDaysBeforeDueDate'];
    responsible: AuditSchedule['responsible'];
    auditSourceType?: AuditSourceType;
    repeatRule?: string;
  }) => {
    const startedAt = new Date();
    dispatch(
      addAuditSchedule({
        orgId,
        groupId,
        data: {
          ...data,
          createBy: {
            id: userId,
            name: userName,
          },
        } as any, // The saga will handle the full data structure
      })
    );
    const createdAuditId = await findCreatedAuditId({
      templateId: data.templateId,
      dueDate: data.dueDate,
      startedAt,
    });

    if (createdAuditId) {
      handleCloseModal();
      history.push(`/orgs/${orgId}/audit/audits/${createdAuditId}`);
      return;
    }
    alert('Audit blev oprettet, men kunne ikke åbnes automatisk.');
  };

  const handleViewTemplate = (templateId: string, isGroupTemplate: boolean) => {
    if (isGroupTemplate) {
      history.push(`/orgs/${orgId}/audit/templates/${templateId}/readonly`);
    } else {
      history.push(`/orgs/${orgId}/audit/templates/${templateId}`);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          gap: 16,
          overflowX: 'auto',
          paddingBottom: 8,
        }}
      >
        <Card
          style={{
            minWidth: 350,
            height: 200,
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingOutlined style={{ fontSize: 40 }} />
        </Card>
      </div>
    );
  }

  if (allPinnedTemplates.length === 0) {
    return null;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: 16,
          overflowX: 'auto',
          paddingBottom: 8,
        }}
      >
        {allPinnedTemplates.map((template) => {
          const isGroupTemplate = groupAuditTemplates.some((gt) => gt.id === template.id);
          return (
            <div
              className="bg-white rounded-lg min-w-[350px] p-4 border border-gray-100 transition-shadow duration-200 cursor-pointer"
              key={template.id}
            >
              <div className="flex flex-col gap-4 h-full">
                {/* Header with pin icon and template title */}
                <div className="flex items-start gap-2">
                  {/* <PushpinFilled
                    style={{ color: StyleGuide.palette.info, fontSize: 16, marginTop: 2 }}
                  /> */}
                  <div className="flex items-center" style={{ flex: 1 }}>
                    <div className="text-lg font-medium">{template.title}</div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                    >
                      {/* <TemplateTaskCount orgId={orgId} templateId={template.id} /> */}
                      {/* {template.auditTemplateCategory && (
                        <span style={{ fontSize: 12, color: StyleGuide.palette.grey500 }}>
                          • {template.auditTemplateCategory}
                        </span>
                      )} */}
                      {/* {isGroupTemplate && (
                        <span
                          style={{
                            fontSize: 11,
                            color: StyleGuide.palette.grey500,
                            padding: '2px 8px',
                            backgroundColor: StyleGuide.palette.grey100,
                            borderRadius: 4,
                          }}
                        >
                          {t('audit.templates.groupTemplates')}
                        </span>
                      )} */}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}
                  >
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleStartAudit(template.id, isGroupTemplate)}
                      style={{ width: '100%' }}
                    >
                      {t('dashboard.pinnedTemplates.startAudit')}
                    </Button>
                    {/* <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewTemplate(template.id, isGroupTemplate)}
                    style={{ width: '100%' }}
                  >
                    {t('dashboard.pinnedTemplates.viewTemplate')}
                  </Button> */}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selectedDate && selectedTemplateId && (
        <ScheduleModal
          selectedValue={selectedDate}
          initialTemplateId={selectedTemplateId}
          initialAuditSourceType={selectedAuditSourceType}
          isPinnedTemplateFlow
          handleClose={handleCloseModal}
          addAuditSchedule={handleAddAuditSchedule}
        />
      )}
    </>
  );
}
